import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { TFunction } from "i18next";
import type { QueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import { voteAdForCandidateElectionsVoteAdPost } from "../../client";
import { handleApiError } from "../../utils/net/apiError";
import { impactOccurred } from "../../utils/telegram/hapticFeedback";
import { AD_VOTE_COOLDOWN_KEY, AD_VOTE_COOLDOWN_MS } from "./constants";

export type AdControllerRef = ReturnType<NonNullable<typeof window.Adsgram>['init']> | null;

export interface UseAdVoteCooldownsParams {
  playVoteSuccess: () => void;
  queryClient: QueryClient;
  refetch: () => Promise<unknown>;
  setError: Dispatch<SetStateAction<string | null>>;
  t: TFunction;
  triggerConfetti: () => void;
}

export interface UseAdVoteCooldownsResult {
  adControllerRef: React.MutableRefObject<AdControllerRef>;
  adCooldowns: Record<number, number>;
  handleAdVote: (candidateId: number) => Promise<void>;
  isAdCooldown: (candidateId: number) => boolean;
  /** Candidate whose ad-vote is currently in flight; read by the vote list. */
  votingCandidateId: number | null;
}

/**
 * Ad-based free voting: the AdsGram interstitial controller, the per-candidate
 * cooldown map (restored from localStorage/CloudStorage, expired by a 1s tick)
 * and the vote action that writes it. One state machine, one hook.
 *
 * Call site matters: this must be invoked exactly where the AdsGram init effect
 * used to sit, so the three effects below still register after the
 * "visit_city_hall" effect and before the election clock effect.
 */
export function useAdVoteCooldowns({
  playVoteSuccess,
  queryClient,
  refetch,
  setError,
  t,
  triggerConfetti,
}: UseAdVoteCooldownsParams): UseAdVoteCooldownsResult {
  const [adCooldowns, setAdCooldowns] = useState<Record<number, number>>({});
  const [votingCandidateId, setVotingCandidateId] = useState<number | null>(null);
  const adControllerRef = useRef<AdControllerRef>(null);

  // AdsGram interstitial init for ad-based voting
  useEffect(() => {
    if (window.Adsgram) {
      try {
        adControllerRef.current = window.Adsgram.init({ blockId: "int-26194" });
      } catch {
        adControllerRef.current = null;
      }
    }
    return () => {
      adControllerRef.current?.destroy?.();
      adControllerRef.current = null;
    };
  }, []);

  // Restore per-candidate ad cooldowns from localStorage + CloudStorage
  useEffect(() => {
    const restoreCooldowns = () => {
      try {
        const raw = localStorage.getItem(AD_VOTE_COOLDOWN_KEY);
        if (raw) {
          const map = JSON.parse(raw) as Record<string, number>;
          const now = Date.now();
          const active: Record<number, number> = {};
          for (const [cid, ts] of Object.entries(map)) {
            const end = ts + AD_VOTE_COOLDOWN_MS;
            if (end > now) active[Number(cid)] = end;
          }
          setAdCooldowns(active);
        }
      } catch { /* ignore */ }
    };

    // Try CloudStorage first (survives Telegram cache clears)
    try {
      const cs = window.Telegram?.WebApp?.CloudStorage;
      if (cs?.getItem) {
        cs.getItem(AD_VOTE_COOLDOWN_KEY, (_err: string | null, val: string | null) => {
          if (val) {
            try { localStorage.setItem(AD_VOTE_COOLDOWN_KEY, val); } catch { /* */ }
          }
          restoreCooldowns();
        });
      } else {
        restoreCooldowns();
      }
    } catch {
      restoreCooldowns();
    }
  }, []);

  // Tick timer to update cooldown progress every second
  useEffect(() => {
    const hasActive = Object.values(adCooldowns).some(end => Date.now() < end);
    if (!hasActive) return;
    const timer = setInterval(() => {
      setAdCooldowns(prev => {
        const now = Date.now();
        const updated = { ...prev };
        let changed = false;
        for (const [cid, end] of Object.entries(updated)) {
          if (end <= now) {
            delete updated[Number(cid)];
            changed = true;
          }
        }
        return changed ? updated : prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [adCooldowns]);

  // Helper: check if ad cooldown active for a candidate
  const isAdCooldown = (candidateId: number) => {
    const end = adCooldowns[candidateId];
    return end ? Date.now() < end : false;
  };

  // Ad-based free vote (+5 votes, per-candidate cooldown)
  const handleAdVote = async (candidateId: number) => {
    if (!adControllerRef.current) {
      setError(t("Ads not available"));
      return;
    }
    if (votingCandidateId) return;

    // Hide Telegram back button while ad is showing
    try { window.Telegram?.WebApp?.BackButton?.hide(); } catch { /* */ }
    try {
      const result = await adControllerRef.current.show();
      if (result.error) {
        try { window.Telegram?.WebApp?.BackButton?.show(); } catch { /* */ }
        return;
      }
    } catch {
      try { window.Telegram?.WebApp?.BackButton?.show(); } catch { /* */ }
      return; // user closed ad
    }
    try { window.Telegram?.WebApp?.BackButton?.show(); } catch { /* */ }

    setVotingCandidateId(candidateId);
    setError(null);
    try {
      impactOccurred("medium");
      playVoteSuccess();
      await voteAdForCandidateElectionsVoteAdPost({
        body: { candidate_id: candidateId },
        throwOnError: true,
      });
      // Save per-candidate cooldown
      const now = Date.now();
      const newCooldowns = { ...adCooldowns, [candidateId]: now + AD_VOTE_COOLDOWN_MS };
      setAdCooldowns(newCooldowns);
      try {
        // Store as { candidateId: startTimestamp } for localStorage + CloudStorage
        const stored: Record<string, number> = {};
        for (const [cid, end] of Object.entries(newCooldowns)) {
          stored[cid] = end - AD_VOTE_COOLDOWN_MS; // store start, not end
        }
        const json = JSON.stringify(stored);
        localStorage.setItem(AD_VOTE_COOLDOWN_KEY, json);
        // AD_VOTE_COOLDOWN_KEY is not an onboarding key, keep in localStorage only
      } catch { /* */ }
      await queryClient.invalidateQueries({ queryKey: ['userData'] });
      refetch();
      triggerConfetti();
      posthog.capture('election_action', { action_type: 'vote_ad', candidate_id: candidateId });
    } catch (err: unknown) {
      console.error('[CityHallScreen] Error ad vote:', err);
      const apiErr = handleApiError(err, { logLabel: 'voteAd' });
      const extra = apiErr.extra as Record<string, unknown> | undefined;
      if (extra?.error === 'cooldown') {
        const remaining = typeof extra.remaining_seconds === 'number' ? extra.remaining_seconds : 0;
        const cid = typeof extra.candidate_id === 'number' ? extra.candidate_id : candidateId;
        setAdCooldowns(prev => ({ ...prev, [cid]: Date.now() + remaining * 1000 }));
        setError(t("Ad vote available in") + ` ${Math.ceil(remaining / 60)} min`);
        setVotingCandidateId(null);
        return;
      }
      try { window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("error"); } catch { /* */ }
      setError(t("Failed to vote. Please try again."));
    } finally {
      setVotingCandidateId(null);
    }
  };

  return {
    adControllerRef,
    adCooldowns,
    handleAdVote,
    isAdCooldown,
    votingCandidateId,
  };
}
